<template>
  <div class="npc-stage">
    <!-- METADE ESQUERDA: O LUGAR (D16) -->
    <div class="npc-stage__lugar">
      <div class="npc-stage__cenario" :style="{ backgroundImage: cenario ? `url('${cenario}')` : null }" aria-hidden="true" />

      <img
        v-if="retrato"
        class="npc-stage__npc repouso-respira"
        :src="retrato"
        :alt="nome"
      />

      <!--
        Clicar no lojista pede outra fala. O `conversar` do `Shop.vue` já existia e nada o
        alcançava: ninguém emitia `interact`, então o balão só mudava quando o jogador mexia na
        prateleira. Cada lojista tem seis saudações no i18n — dá para ficar puxando conversa, e é
        assim que ele parece pessoa em vez de placa.

        É uma camada sobre o cenário e **não** um botão em volta do retrato: embrulhar a imagem
        mudaria contra quem os 78% de altura dele medem, e a altura do NPC é a correção central do
        D16 — não vale arriscá-la para ganhar um clique. Como camada, o alvo é a meia tela inteira,
        que no telefone é a diferença entre acertar e não acertar.
      -->
      <button
        v-if="retrato"
        class="npc-stage__gatilho"
        type="button"
        :aria-label="nome"
        @click="$emit('interact')"
      />

      <!--
        O DIÁLOGO É UM BALÃO SOBRE O NPC, não uma barra atravessando o topo (D16, 5.2).

        A caixa de hoje é "uma barra vazia atravessando o topo inteiro para caber uma frase curta —
        noventa por cento de espaço morto". Um balão tem o tamanho da fala e aponta para quem fala,
        que é o que faz o NPC virar personagem em vez de avatar.
      -->
      <Transition name="balao">
        <div v-if="fala" :key="fala" class="npc-stage__balao" role="status" aria-live="polite">
          <p class="npc-stage__fala t-corpo">{{ fala }}</p>
          <span class="npc-stage__bico" aria-hidden="true" />
        </div>
      </Transition>

      <p v-if="nome" class="npc-stage__nome t-rotulo">{{ nome }}</p>
    </div>

    <!-- METADE DIREITA: O PAINEL (D16) -->
    <div class="npc-stage__painel">
      <slot />
    </div>
  </div>
</template>

<script>
/**
 * 2.11 — `NpcStage`: **meia tela de lugar, meia tela de painel** (D16).
 *
 * ## Por que a loja de hoje é feia — e não é gosto, é estrutura
 *
 * - **O NPC é uma miniatura.** O Velho João é um quadradinho no canto superior esquerdo, na frente
 *   de um `shop-interior.png` de 3,5 MB em tela cheia. **O personagem é a menor coisa da tela e o
 *   fundo é a maior** — exatamente ao contrário do que deveria.
 * - **A caixa de diálogo é uma barra vazia** atravessando o topo inteiro para caber uma frase curta.
 * - **Os itens são um paredão de cards**, catorze de uma vez, cada um repetindo ícone, nome,
 *   descrição longa, dois atributos, preço, selo de nível e botão.
 *
 * Resultado: sem ponto focal, sem sensação de lugar, e ler dá trabalho.
 *
 * ## O modelo
 *
 * > **A loja é meia tela de LUGAR e meia tela de PAINEL.**
 *
 * **Esquerda, o lugar:** o lojista no balcão dele, **em tamanho de gente**, numa loja de verdade. É
 * isso que faz parecer uma lojinha.
 *
 * **Direita, o painel:** abas de categoria em ícone, um card de detalhe do item selecionado, e uma
 * grade de encaixes pequenos com todo o resto.
 *
 * ## Por que isso funciona e o paredão não
 *
 * **Só um item por vez é descrito.** A grade mostra ícone e raridade; o texto mora no card de
 * detalhe. Assim o tamanho da descrição **para de multiplicar por catorze** — que é o mecanismo
 * exato do paredão.
 *
 * ## Onde mais ele vale
 *
 * Não é só a loja do Velho João. O D24 manda a **barraca do clã** seguir o mesmo modelo — *"porque
 * é uma loja, e a consistência com a do Velho João é o que faz o jogo parecer um jogo só"* — e o
 * D29b manda **a casa** seguir também, com "o lugar" sendo o interior da sua casa.
 *
 * ## O telefone
 *
 * A meia tela vira **uma faixa em cima**. O NPC continua grande dentro dela, porque encolher o NPC
 * no telefone seria voltar à miniatura que o D16 está corrigindo — e o telefone é onde a maioria
 * joga.
 */
export default {
  name: 'NpcStage',
  /** Clicar no lojista. Quem escuta decide o que ele diz — aqui o palco só avisa. */
  emits: ['interact'],
  props: {
    /** O retrato do NPC. Em tamanho de gente, não em avatar. */
    retrato: { type: String, default: '' },
    nome: { type: String, default: '' },
    /** O interior. O do Velho João fica (D8): foi desenhado para ele. */
    cenario: { type: String, default: '' },
    /**
     * A fala. **Reativa** (D16, 5.2): o NPC comenta o que você selecionou, a compra, e o fato de
     * faltar ouro. Trocar esta prop troca o balão com transição — é assim que o NPC ganha o lugar
     * dele em vez de repetir uma frase de boas-vindas.
     */
    fala: { type: String, default: '' },
  },
};
</script>

<style scoped>
.npc-stage {
  display: flex;
  flex-direction: column;
  gap: var(--e-4);
  min-height: 0;
}

@media (min-width: 900px) {
  .npc-stage {
    flex-direction: row;
    align-items: stretch;
  }

  .npc-stage__lugar,
  .npc-stage__painel {
    flex: 1;
    min-width: 0;
  }
}

.npc-stage__lugar {
  position: relative;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  min-height: 200px;
  overflow: hidden;
  isolation: isolate;
  border: 2px solid var(--madeira-escura);
}

@media (min-width: 900px) {
  .npc-stage__lugar {
    min-height: 420px;
  }
}

.npc-stage__cenario {
  position: absolute;
  inset: 0;
  z-index: var(--z-cenario);
  background-position: center;
  background-size: cover;
  image-rendering: pixelated;
  background-color: var(--agua-funda);
}

/**
 * O NPC EM TAMANHO DE GENTE — a correção central do D16.
 *
 * 78% da altura do palco, ancorado na base. Ele fica maior que qualquer outra coisa na metade
 * esquerda, que é a inversão exata da tela de hoje (o personagem menor, o fundo maior).
 */
.npc-stage__npc {
  position: relative;
  z-index: var(--z-conteudo);
  height: 78%;
  max-height: 380px;
  width: auto;
  object-fit: contain;
  object-position: bottom;
  image-rendering: pixelated;
  filter: drop-shadow(0 4px 0 rgba(0, 0, 0, 0.4));
}

/**
 * O gatilho de conversa: a metade do lugar inteira, invisível.
 *
 * Fica **acima do cenário e do NPC** e **abaixo do balão** — o balão é `--z-elevado`, então ele
 * continua legível e selecionável por cima. Nada mais nesta metade é interativo, então a camada não
 * rouba clique de ninguém. Sem borda, sem fundo: o que se vê continua sendo só o lojista; o foco
 * visível fica, porque o teclado também tem direito de puxar conversa.
 */
.npc-stage__gatilho {
  position: absolute;
  inset: 0;
  z-index: var(--z-conteudo);
  padding: 0;
  border: 0;
  background: none;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}

.npc-stage__gatilho:focus-visible {
  outline: 3px solid var(--papel-luz);
  outline-offset: -4px;
}

.npc-stage__balao {
  position: absolute;
  z-index: var(--z-elevado);
  top: var(--e-3);
  left: var(--e-3);
  right: var(--e-3);
  max-width: 34ch;
  margin: 0 auto;

  /* O balão está ACIMA do gatilho, e clicar no balão para ouvir a próxima fala é o gesto que todo
   * jogo de RPG ensinou. Deixando o clique atravessar, a meia tela inteira — cenário, lojista e
   * balão — é um alvo só. O preço é não poder selecionar o texto, o que num balão de diálogo não
   * é preço. */
  pointer-events: none;

  background: var(--papel-frio);
  color: var(--tinta);
  border: 3px solid var(--madeira-escura);
  padding: var(--e-2) var(--e-3);
  box-shadow: var(--sombra-2);
}

.npc-stage__fala {
  margin: 0;
}

/* O bico do balão: um losango girado, com a mesma borda. É o que aponta para quem fala — sem ele o
 * balão é uma caixa de texto qualquer flutuando. */
.npc-stage__bico {
  position: absolute;
  bottom: -8px;
  left: 50%;
  width: 12px;
  height: 12px;
  transform: translateX(-50%) rotate(45deg);
  background: var(--papel-frio);
  border-right: 3px solid var(--madeira-escura);
  border-bottom: 3px solid var(--madeira-escura);
}

.npc-stage__nome {
  position: absolute;
  z-index: var(--z-elevado);
  bottom: var(--e-2);
  left: var(--e-2);
  background: rgba(14, 27, 38, 0.8);
  padding: 2px var(--e-2);
  color: var(--texto);
  /* Pela mesma razão do balão: a placa com o nome não é um buraco no alvo de conversa. */
  pointer-events: none;
}

.npc-stage__painel {
  display: flex;
  flex-direction: column;
  gap: var(--e-3);
  min-width: 0;
}

/* A fala entra de baixo e sai por cima, devagar. É o D0: nada aparece de estalo. */
.balao-enter-active,
.balao-leave-active {
  transition: opacity var(--t-medio) var(--curva-suave), transform var(--t-medio) var(--curva-entrada);
}

.balao-enter-from {
  opacity: 0;
  transform: translateY(8px);
}

.balao-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

/* Duas falas nunca coexistem: sem isto, a que sai e a que entra empilham e o balão dobra de altura
 * no meio da transição. */
.balao-leave-active {
  position: absolute;
}
</style>
